import { qualifiesAsTourPackage, type ItineraryComponentOut } from '@utazzo/shared';
import { ChevronDown, ChevronRight, Receipt } from 'lucide-react';
import React, { useState } from 'react';

import {
  useItineraryComponents,
  useItineraryDetail,
  useMarkupSuggestion,
} from '../use-itineraries';

import { isFdLandPackage, isFdSupplement } from './ComponentCard';
import { computeItineraryTaxTotals } from './pricing-totals';

import { useCurrentUser } from '@/features/auth/use-auth';
import { useDestination } from '@/features/destinations/use-destinations';
import { useLead } from '@/features/leads/use-leads';
import { useAppConfig } from '@/features/settings/use-settings';

/**
 * Live pricing sidebar (Phase 5). Sits on the Days tab and recomputes from the
 * day components as the agent edits: land package / flights / add-ons, then
 * subtotal -> markup -> discount -> TCS -> grand total. Discount + TCS come
 * from the Pricing tab; visa/insurance amounts are priced at quote time.
 *
 * 2026-07-07. The top per-category listing (Hotels / Flights / Activities /
 * Transfers + Visa / Insurance "at quote" markers) was hidden per operator
 * feedback - the subtotal breakdown below already covers what's needed, and
 * the removed section duplicated the day cards' own line items. Card title,
 * subtotal section, markup/discount/GST/TCS section, and grand-total footer
 * are unchanged.
 */

const sumBy = (
  comps: ItineraryComponentOut[],
  kinds: string[],
  field: 'costPaise' | 'markupPaise',
): number =>
  comps.filter((c) => kinds.includes(c.kind)).reduce((s, c) => s + (Number(c[field]) || 0), 0);

const fmt = (paise: number, currency: string): string => {
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(paise / 100);
  } catch {
    return `${currency} ${(paise / 100).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }
};

export const PricingSidebar = ({ itineraryId }: { itineraryId: string }): React.JSX.Element => {
  const detailQuery = useItineraryDetail(itineraryId);
  const componentsQuery = useItineraryComponents(itineraryId);
  const comps = componentsQuery.data ?? [];
  const detail = detailQuery.data ?? null;

  // Per-category detail (markup + per-person split) is collapsed by default;
  // each cost row has a chevron to reveal its own markup and per-person lines.
  const [expandedRows, setExpandedRows] = useState<ReadonlySet<string>>(new Set());
  const toggleRow = (label: string): void =>
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  const currency = detail?.currencyCode ?? 'INR';
  const config = useAppConfig().data ?? null;
  // Markup (global per-category breakdown) is admin-only. Sales sees only their
  // own overall markup, never the global/product split.
  const role = useCurrentUser()?.role;
  const isAdmin = role === 'ADMIN';
  // Sales lead / agent must not see GST or TCS line items.
  const canSeeTax = role !== 'SALES_LEAD' && role !== 'SALES_AGENT';

  const lead = useLead(detail?.leadId ?? undefined).data ?? null;
  const dest = useDestination(lead?.destinationCity?.id ?? undefined).data ?? null;
  // TCS applies only to an INTERNATIONAL "overseas tour package" - >=2 of
  // {flight}/{hotel,cruise}/{activity,transfer,VAS}. A standalone air ticket or
  // standalone stay is not a package, so no TCS. Mirrors the server.
  const applyTcs =
    dest?.isInternational !== false && qualifiesAsTourPackage(comps.map((c) => c.kind));
  const gstRate = detail?.gstRatePercent ?? config?.tax.gstRatePercent ?? 5;
  const tcsRate = detail?.tcsRatePercent ?? Number(config?.tax.tcsRatePercent ?? '2');

  const hotelCost = sumBy(comps, ['HOTEL'], 'costPaise');
  // Cruise-as-stay (Phase 2): a cruise cabin is accommodation like a hotel
  // room - it gets its own bucket below but folds into the same "Land
  // package" display total as hotel/activity/transfer.
  const cruiseCost = sumBy(comps, ['CRUISE'], 'costPaise');
  const flightCost = sumBy(comps, ['FLIGHT'], 'costPaise');
  const activityCost = sumBy(comps, ['ACTIVITY'], 'costPaise');
  const transferCost = sumBy(comps, ['TRANSFER'], 'costPaise');
  // Visa cost is FX-converted server-side into the itinerary currency
  // (see itineraries.service#computeVisaCostPaise). Client just reads it.
  const visaCost = Number(detail?.visaCostPaise ?? 0) || 0;
  const insuranceCost = Number(detail?.insuranceCostPaise ?? 0) || 0;
  const addOnCost = sumBy(comps, ['VAS'], 'costPaise') + visaCost + insuranceCost;
  const rawSubtotal = hotelCost + cruiseCost + activityCost + transferCost + flightCost + addOnCost;

  // Per-category product markup source of truth:
  //  - If the itinerary already has `markupByCategory` stored -> use it
  //    verbatim. Copies and saved drafts both fall through this branch, so
  //    a copy-forward version's baked (discount+upsell folded) markup shows
  //    up on the sidebar untouched by any live recompute.
  //  - Only fetch the LIVE suggestion when the itinerary is DRAFT/IN_PROGRESS
  //    AND has no stored markup yet (first-from-lead or blank version whose
  //    PricingStep hasn't run its initial seed). In every other case, we
  //    skip the suggestion query so a copy-forward pricing tab never briefly
  //    flashes fresh live values.
  const isDraftLive = detail?.status === 'DRAFT' || detail?.status === 'IN_PROGRESS';
  const storedMbcRaw = detail?.markupByCategory ?? null;
  const needsSuggestion = isDraftLive && !storedMbcRaw;
  const suggestion = useMarkupSuggestion(needsSuggestion ? itineraryId : null).data ?? null;
  const mbc = storedMbcRaw ?? suggestion?.markupByCategory ?? {};
  const hotelProductMarkup = Number(mbc.HOTEL ?? 0) || 0;
  const cruiseProductMarkup = Number(mbc.CRUISE ?? 0) || 0;
  const activityProductMarkup = Number(mbc.ACTIVITY ?? 0) || 0;
  const transferProductMarkup = Number(mbc.TRANSFER ?? 0) || 0;
  const flightProductMarkup = Number(mbc.FLIGHT ?? 0) || 0;
  const addOnProductMarkup = Number(mbc.ADDON ?? 0) || 0;
  const globalMarkup =
    hotelProductMarkup +
    cruiseProductMarkup +
    activityProductMarkup +
    transferProductMarkup +
    flightProductMarkup +
    addOnProductMarkup;

  // Agent's overall markup - not category-specific, distributed proportionally
  // across the FIVE line-item buckets so per-item totals honestly reflect what
  // the customer will see. `agentMarkupPaise` is read directly (not derived
  // from netMarkup - Σ mbc) so any drift in the category rollup can't silently
  // understate the agent markup here.
  const agentMarkup = Number(detail?.agentMarkupPaise ?? 0) || 0;
  const shareOf = (cost: number): number =>
    rawSubtotal > 0 ? Math.round((agentMarkup * cost) / rawSubtotal) : 0;
  const hotelAgentShare = shareOf(hotelCost);
  const cruiseAgentShare = shareOf(cruiseCost);
  const activityAgentShare = shareOf(activityCost);
  const transferAgentShare = shareOf(transferCost);
  const flightAgentShare = shareOf(flightCost);
  // Add-ons absorbs the rounding remainder so the shares sum exactly to
  // agentMarkup (rather than being off by 1-2 paise due to rounding).
  const addOnAgentShare =
    agentMarkup -
    hotelAgentShare -
    cruiseAgentShare -
    activityAgentShare -
    transferAgentShare -
    flightAgentShare;

  const hotelTotal = hotelCost + hotelProductMarkup + hotelAgentShare;
  const cruiseTotal = cruiseCost + cruiseProductMarkup + cruiseAgentShare;
  const activityTotal = activityCost + activityProductMarkup + activityAgentShare;
  const transferTotal = transferCost + transferProductMarkup + transferAgentShare;
  const flightTotal = flightCost + flightProductMarkup + flightAgentShare;
  const addOnTotal = addOnCost + addOnProductMarkup + addOnAgentShare;
  const landPackageTotal = hotelTotal + cruiseTotal + activityTotal + transferTotal;
  const subtotal = landPackageTotal + flightTotal + addOnTotal;

  // Fixed-departure display categories. The land price is a VAS (generic ADDON
  // bucket) and supplements are TRANSFERs, so the FIT buckets above mislabel
  // them ("Land package" would show the supplement, "Add-ons" the land). Split
  // by FD semantics and spread the total markup proportionally by cost - the FD
  // markup is an overall target, so this is faithful and `subtotal` is unchanged.
  const isFd = detail?.kind === 'FIXED_DEPARTURE';
  const sumCostWhere = (pred: (c: ItineraryComponentOut) => boolean): number =>
    comps.filter(pred).reduce((s, c) => s + (Number(c.costPaise) || 0), 0);
  const fdLandCost = sumCostWhere(isFdLandPackage);
  // Vehicle supplements are their OWN category - never folded into the land
  // package. Visa/insurance/other VAS are the add-ons.
  const fdSupplementCost = sumCostWhere(isFdSupplement);
  const fdAddOnCost =
    sumCostWhere((c) => c.kind === 'VAS' && !isFdLandPackage(c)) + visaCost + insuranceCost;
  const fdTotalMarkup = globalMarkup + agentMarkup;
  // Markup per FD line = the ACTUAL per-category bucket, NOT a proportional
  // spread - so flights show their own rule (e.g. global 2%) while land and
  // supplements show the FD rate. Land + Add-ons share the ADDON/HOTEL/ACTIVITY
  // buckets (all at the FD rate), split by cost; the Add-ons row absorbs the
  // remainder (incl. any agent markup) so the FD rows still sum to `subtotal`.
  const fdFlightMarkupShare = flightProductMarkup;
  const fdSupplementMarkup = transferProductMarkup;
  const fdLandAddonMk = hotelProductMarkup + activityProductMarkup + addOnProductMarkup;
  const fdLandCostAndAddon = fdLandCost + fdAddOnCost;
  const fdLandMarkup =
    fdLandCostAndAddon > 0 ? Math.round((fdLandAddonMk * fdLandCost) / fdLandCostAndAddon) : 0;
  // Add-ons absorbs the remainder so the FD rows sum to `subtotal` (see
  // `fdAddOnMarkup` where the component rows are built).

  // Draft / in-progress: recompute discounts from saved % × current final
  // markup (same formula as PricingStep) so Preview stays in sync while
  // editing. Sent-to-customer and later: freeze every money line from the
  // DB snapshot the customer was quoted.
  const useFrozenPricing = Boolean(detail) && !isDraftLive;

  // Discount/Upsell are fixed rupee amounts now (no longer percent-of-markup),
  // so the draft preview uses the STORED amount directly - matching PricingStep,
  // which sets a fixed amount (or locks a legacy one) rather than a live percent.
  const storedDiscount = Number(detail?.discountPaise ?? 0) || 0;
  const storedAdditionalDiscount = Number(detail?.additionalDiscountPaise ?? 0) || 0;

  const liveTotals = computeItineraryTaxTotals({
    netCostPaise: rawSubtotal,
    // Flight base fare - excluded from the TCS base (TCS on flight margin only).
    flightComponentPaise: flightCost,
    globalMarkupPaise: globalMarkup,
    agentMarkupPaise: agentMarkup,
    discountPaise: storedDiscount,
    additionalDiscountPaise: storedAdditionalDiscount,
    gstRatePercent: gstRate,
    tcsRatePercent: tcsRate,
    applyTcs,
  });

  const discount = useFrozenPricing ? storedDiscount : liveTotals.effectiveDiscountPaise;
  const additionalDiscount = useFrozenPricing
    ? storedAdditionalDiscount
    : liveTotals.effectiveAdditionalDiscountPaise;
  const gst = useFrozenPricing ? Number(detail?.gstPaise ?? 0) || 0 : liveTotals.gstPaise;
  const tcs = useFrozenPricing ? Number(detail?.tcsPaise ?? 0) || 0 : liveTotals.tcsPaise;
  const hasTcs = applyTcs;
  const grandTotal = useFrozenPricing
    ? Number(detail?.grandTotalPaise ?? 0) || 0
    : liveTotals.grandTotalPaise;
  // Signed rounding adjustment folded into the whole-rupee grand total. Shown as
  // its own line so the breakdown still ties out to the rounded total.
  const roundOff = useFrozenPricing
    ? Number(detail?.roundOffPaise ?? 0) || 0
    : liveTotals.roundOffPaise;
  // Frozen quotes also pin the headline buckets to the saved net so the
  // sidebar grand total ties out even if live component rollups drift.
  const displaySubtotal = useFrozenPricing
    ? (Number(detail?.netCostPaise ?? 0) || 0) + (Number(detail?.netMarkupPaise ?? 0) || 0)
    : subtotal;
  const displayGlobalMarkup = useFrozenPricing
    ? (Number(detail?.netMarkupPaise ?? 0) || 0) - (Number(detail?.agentMarkupPaise ?? 0) || 0)
    : globalMarkup;
  const displayAgentMarkup = useFrozenPricing
    ? Number(detail?.agentMarkupPaise ?? 0) || 0
    : agentMarkup;
  const displayNetCost = useFrozenPricing ? Number(detail?.netCostPaise ?? 0) || 0 : rawSubtotal;

  // Pax split for the per-person / per-child lines. We only have a single
  // per-component cost from the components (no per-adult vs per-child split),
  // so both rows share the same per-head value; per-child is skipped when
  // there are no children on the trip.
  const adults = detail?.adultsCount ?? 0;
  const children = detail?.childrenCount ?? 0;
  const heads = Math.max(1, adults + children);
  const perHead = (paise: number): number => Math.round(paise / heads);
  const hasChildren = children > 0;

  // Per-component cost + markup rows (admin-only detail). FD itineraries use the
  // FD categorisation (Land package / Vehicle supplements / Flights / Add-ons);
  // FIT uses the product buckets. Each row carries its own cost and its product
  // (system) markup; rows with zero cost are dropped so the sidebar stays tight.
  const fdAddOnMarkup = fdTotalMarkup - fdLandMarkup - fdSupplementMarkup - fdFlightMarkupShare;
  const componentRows: ReadonlyArray<{ label: string; cost: number; markup: number }> = (
    isFd
      ? [
          { label: 'Land package', cost: fdLandCost, markup: fdLandMarkup },
          { label: 'Vehicle supplements', cost: fdSupplementCost, markup: fdSupplementMarkup },
          { label: 'Flights', cost: flightCost, markup: fdFlightMarkupShare },
          { label: 'Add-ons', cost: fdAddOnCost, markup: fdAddOnMarkup },
        ]
      : [
          { label: 'Hotels', cost: hotelCost, markup: hotelProductMarkup },
          { label: 'Cruise', cost: cruiseCost, markup: cruiseProductMarkup },
          { label: 'Flights', cost: flightCost, markup: flightProductMarkup },
          { label: 'Transfers', cost: transferCost, markup: transferProductMarkup },
          { label: 'Activities', cost: activityCost, markup: activityProductMarkup },
          { label: 'Add-ons', cost: addOnCost, markup: addOnProductMarkup },
        ]
  ).filter((r) => r.cost > 0);

  // Net Selling Price = Net Cost + all markup (reconciles with the component
  // rows above). Final Selling Price (TTV) = Net Selling - Discount + Upsell,
  // i.e. the taxable base before GST/TCS.
  const netSellingPrice = displaySubtotal;
  const finalSellingPriceTtv = displaySubtotal - discount + additionalDiscount;

  // Percent label helper, e.g. "(15.0%)". Null when either side is non-positive.
  const pctLabel = (part: number, base: number): string =>
    base > 0 && part > 0 ? ` (${((part / base) * 100).toFixed(1)}%)` : '';

  return (
    <aside className="lg:sticky lg:top-4">
      <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white">
        <header className="flex items-center gap-2 border-b border-zinc-100 px-4 py-3">
          <Receipt className="text-primary h-4 w-4" aria-hidden />
          <h3 className="text-sm font-semibold tracking-tight text-zinc-900">Pricing</h3>
          <span className="ml-auto text-[11px] font-medium text-zinc-400">{currency}</span>
        </header>

        <div className="space-y-3 px-4 py-4">
          {/* Per-component cost + its system markup (admin-only detail). Each
              component shows cost, then its markup (with %), then the per-person /
              per-child split. Sales never sees this section - they start at Net
              Selling Price below. */}
          {isAdmin && componentRows.length > 0 && (
            <div className="space-y-2">
              {componentRows.map((r) => {
                const expanded = expandedRows.has(r.label);
                return (
                  <div key={r.label} className="space-y-0.5">
                    <button
                      type="button"
                      onClick={() => toggleRow(r.label)}
                      aria-expanded={expanded}
                      aria-label={`${expanded ? 'Hide' : 'Show'} ${r.label} markup and per person`}
                      className="flex w-full items-baseline justify-between gap-3 text-left"
                    >
                      <span className="flex items-center gap-1 text-xs font-semibold text-zinc-700">
                        {expanded ? (
                          <ChevronDown className="h-3 w-3 shrink-0 text-zinc-400" aria-hidden />
                        ) : (
                          <ChevronRight className="h-3 w-3 shrink-0 text-zinc-400" aria-hidden />
                        )}
                        <span className="truncate">{r.label} cost</span>
                      </span>
                      <span className="shrink-0 text-sm font-semibold text-zinc-900 tabular-nums">
                        {fmt(r.cost, currency)}
                      </span>
                    </button>
                    {expanded && (
                      <div className="pl-3">
                        <Row label={`Markup${pctLabel(r.markup, r.cost)}`} muted>
                          + {fmt(r.markup, currency)}
                        </Row>
                        <LineItem
                          label=""
                          total={r.cost + r.markup}
                          currency={currency}
                          perHead={perHead}
                          hasChildren={hasChildren}
                          headless
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Net cost -> system markup -> net selling price. Net Cost + System
              markup (admin-only read-outs, already summed into the component rows)
              roll up to the Net Selling Price every role sees. */}
          <div className="space-y-1.5 border-t border-zinc-100 pt-3">
            {isAdmin && (
              <>
                <Row label="Net Cost">{fmt(displayNetCost, currency)}</Row>
                <Row label={`System markup${pctLabel(displayGlobalMarkup, displayNetCost)}`}>
                  {fmt(displayGlobalMarkup, currency)}
                </Row>
                {/* Overall (agent) markup is deprecated; only shown for legacy
                    itineraries that still carry a non-zero stored value. */}
                {displayAgentMarkup !== 0 && (
                  <Row label={`Overall markup${pctLabel(displayAgentMarkup, displayNetCost)}`}>
                    {fmt(displayAgentMarkup, currency)}
                  </Row>
                )}
              </>
            )}
            <Row label="Net Selling Price" strong>
              {fmt(netSellingPrice, currency)}
            </Row>
            <Row label="Discount">- {fmt(discount, currency)}</Row>
            <Row label="Upsell">+ {fmt(additionalDiscount, currency)}</Row>
          </div>

          {/* Final Selling Price = the taxable base before GST / TCS. The "(TTV)"
              tag is internal jargon, shown to admins only. */}
          <div className="border-t border-zinc-200 pt-3">
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-semibold text-zinc-900">
                Final Selling Price{isAdmin ? ' (TTV)' : ''}
              </span>
              <span className="text-sm font-semibold text-zinc-900 tabular-nums">
                {fmt(finalSellingPriceTtv, currency)}
              </span>
            </div>
          </div>

          {/* Taxes + round off. GST / TCS are hidden from sales roles. */}
          {(canSeeTax || roundOff !== 0) && (
            <div className="space-y-1.5 border-t border-zinc-100 pt-3">
              {canSeeTax && <Row label={`GST @ ${gstRate}%`}>{fmt(gst, currency)}</Row>}
              {canSeeTax && hasTcs && <Row label={`TCS @ ${tcsRate}%`}>{fmt(tcs, currency)}</Row>}
              {roundOff !== 0 && (
                <Row label="Round off">
                  {roundOff > 0 ? '+ ' : '- '}
                  {fmt(Math.abs(roundOff), currency)}
                </Row>
              )}
            </div>
          )}

          <div className="border-t border-zinc-200 pt-3">
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-semibold text-zinc-900">Grand Total</span>
              <span className="text-base font-bold text-zinc-900 tabular-nums">
                {fmt(grandTotal, currency)}
              </span>
            </div>
            {/* Admin sees the "(Incl. GST & TCS)" note. Non-admins don't see the
                GST/TCS breakdown, so instead surface how much TCS the grand total
                already includes (only when TCS applies). */}
            {isAdmin ? (
              <p className="mt-0.5 text-[10px] text-zinc-400">(Incl. GST &amp; TCS)</p>
            ) : (
              hasTcs &&
              tcs > 0 && (
                <p className="mt-0.5 text-[10px] text-zinc-400">Incl. TCS {fmt(tcs, currency)}</p>
              )
            )}
          </div>
        </div>
      </div>
    </aside>
  );
};

const Row = ({
  label,
  icon,
  children,
  strong,
  muted,
}: {
  label: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  strong?: boolean;
  muted?: boolean;
}): React.JSX.Element => (
  <div className="flex items-baseline justify-between gap-3">
    <dt
      className={
        'flex items-center gap-1.5 text-xs ' +
        (strong ? 'font-semibold text-zinc-700' : 'text-zinc-500')
      }
    >
      {icon}
      <span className="truncate">{label}</span>
    </dt>
    <dd
      className={
        'shrink-0 text-sm tabular-nums ' +
        (strong ? 'font-semibold text-zinc-900' : muted ? 'text-zinc-400' : 'text-zinc-700')
      }
    >
      {children}
    </dd>
  </div>
);

/**
 * A single line item inside the pricing breakdown - shows the item name +
 * total on top, then a small per-person (and per-child, when kids exist) row
 * indented under it. `headless` skips the name/total header for cases where
 * the parent already rendered a total row (Flights / Add-ons buckets).
 */
const LineItem = ({
  label,
  total,
  currency,
  perHead,
  hasChildren,
  headless,
}: {
  label: string;
  total: number;
  currency: string;
  perHead: (paise: number) => number;
  hasChildren: boolean;
  headless?: boolean;
}): React.JSX.Element => (
  <div className="space-y-0.5">
    {!headless && (
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-xs text-zinc-600">{label}</span>
        <span className="text-xs text-zinc-700 tabular-nums">{fmt(total, currency)}</span>
      </div>
    )}
    <div className="flex items-baseline justify-between gap-3 pl-2">
      <span className="text-[10px] text-zinc-400">Per person</span>
      <span className="text-[11px] text-zinc-500 tabular-nums">
        {fmt(perHead(total), currency)}
      </span>
    </div>
    {hasChildren && (
      <div className="flex items-baseline justify-between gap-3 pl-2">
        <span className="text-[10px] text-zinc-400">Per child</span>
        <span className="text-[11px] text-zinc-500 tabular-nums">
          {fmt(perHead(total), currency)}
        </span>
      </div>
    )}
  </div>
);
